import { Switch, TableColumnsType, Tooltip } from "antd";
import moment from "moment";
import {
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
} from "@ant-design/icons";

import { ICashAllowance, ICountry, IVersion } from "@types";
import { checkPermission, formatFirstLetterUpperRestLower } from "@/utils";
import { permissions } from "@/router";
import { dateFormat } from "@/constants";
import { DownloadOutlined } from "@ant-design/icons";
import { formatNumberWithCommas } from "@/utils/formatNumberWithCommas";

export const getColumns = ({
  removeBenefitsPlan,
  cashAllowance_id,
  isDeleting,
  editBenefitsPlan,
}: {
  isDeleting?: boolean;
  removeBenefitsPlan: (id: number) => Promise<void>;
  cashAllowance_id?: React.MutableRefObject<any>;
  editBenefitsPlan: (cashAllowance: ICashAllowance) => void;
}): TableColumnsType<any> => [
  {
    title: "Benefit plan name",
    dataIndex: "name",
    key: "name",
    width: 200,
    render: (name: ICountry) => <div>{name || "N/A"}</div>,
  },
  {
    title: "Category",
    dataIndex: "category",
    key: "category",
    width: 200,
    render: (name: string) => (
      <div>{formatFirstLetterUpperRestLower(name) || "N/A"}</div>
    ),
  },
  {
    title: "Benefit Type",
    dataIndex: "detail",
    key: "detail",
    width: 200,
    render: detail => (
      <div>
        {formatFirstLetterUpperRestLower(detail?.benefit_type) || "N/A"}
      </div>
    ),
  },
  {
    title: "calculation basis",
    dataIndex: "detail",
    key: "detail",
    width: 200,
    render: detail => (
      <div>
        {formatFirstLetterUpperRestLower(detail?.calculation_basis) || "N/A"}
      </div>
    ),
  },
  {
    title: "value",
    dataIndex: "detail",
    key: "detail",
    width: 150,
    render: (detail: any) =>
      `${formatNumberWithCommas(detail?.value) || "N/A"}`,
  },
  {
    title: "employer cost",
    dataIndex: "detail",
    key: "detail",
    width: 150,
    render: (detail: any) =>
      `${formatNumberWithCommas(detail?.employer_cost) || "N/A"}`,
  },
  {
    title: "grades",
    key: "grades",
    width: 400,
    render: (plan: any) => {
      const gradesString = plan?.grades?.join(", ") || "N/A";
      return (
        <div>
          {gradesString?.length > 30 ? (
            <Tooltip title={gradesString}>
              <span className="truncate cursor-pointer">
                {gradesString?.slice(0, 30)}...
              </span>
            </Tooltip>
          ) : (
            gradesString
          )}
        </div>
      );
    },
  },
  {
    title: "Countries",
    key: "countries",
    width: 400,
    render: (countries: any) => {
      const countriesString =
        countries?.countries?.length > 0
          ? countries?.countries
              ?.map(({ country }: { country: string }) => country)
              ?.join(", ") || "N/A"
          : "N/A";

      return (
        <div>
          {countriesString?.length > 30 ? (
            <Tooltip title={countriesString}>
              <span className="truncate cursor-pointer">
                {countriesString?.slice(0, 30)}...
              </span>
            </Tooltip>
          ) : (
            countriesString
          )}
        </div>
      );
    },
  },
  {
    title: "city",
    dataIndex: "city",
    key: "200",
    width: 400,
    render: (city: string) => {
      return <div>{city || "N/A"}</div>;
    },
  },

  {
    title: "description",
    dataIndex: "detail",
    key: "detail",
    width: 150,
    render: (detail: any) => (
      <>
        {detail?.description?.length > 20 ? (
          <Tooltip title={detail?.description || "N/A"}>
            <span className="truncate">
              {detail?.description
                ? detail.description?.slice(0, 20) +
                  (detail.description?.length > 20 ? "..." : "")
                : "N/A"}
            </span>
          </Tooltip>
        ) : (
          <Tooltip title={detail?.description || "N/A"}>
            <span className="truncate">{detail?.description || "N/A"}</span>
          </Tooltip>
        )}
      </>
    ),
  },
  ...(!checkPermission([
    permissions.UPDATE_BENEFITS_PLAN,
    permissions.DELETE_BENEFITS_PLAN,
  ])
    ? []
    : ([
        {
          title: "Actions",
          key: "actions",
          width: 160,
          fixed: "right",
          render: (cashAllowance: ICashAllowance) => (
            <div className="flex items-center gap-2">
              {checkPermission(permissions.UPDATE_BENEFITS_PLAN) && (
                <div
                  onClick={() => editBenefitsPlan(cashAllowance)}
                  className="table__action__btn table__action__btn--client"
                >
                  <EditOutlined style={{ color: "#1A8245" }} /> Edit
                </div>
              )}

              {checkPermission(permissions.DELETE_BENEFITS_PLAN) && (
                <div
                  onClick={() => removeBenefitsPlan(cashAllowance?.id!)}
                  className="table__action__btn table__action__btn--delete"
                >
                  {isDeleting &&
                  cashAllowance?.id === cashAllowance_id?.current ? (
                    <LoadingOutlined color="#B62424" className="spinner" />
                  ) : (
                    <>
                      <DeleteOutlined style={{ color: "#B62424" }} /> Delete
                    </>
                  )}
                </div>
              )}
            </div>
          ),
        },
      ] as any)),
];

export const getVersionsColumns = ({
  handleActive,
  versionId,
  download,
}: {
  handleActive: (id: number) => Promise<void>;
  versionId: number | null;
  download: (id?: number | undefined) => void;
}): TableColumnsType<any> => [
  {
    title: "created at",
    dataIndex: "created_at",
    key: "created_at",
    width: "35%",
    render: (date: string) => <div>{moment(date).format(dateFormat)}</div>,
  },
  ...(!checkPermission(permissions.CREATE_BENEFITS_PLAN)
    ? []
    : ([
        {
          title: "Active",
          key: "active",
          width: "15%",
          align: "center",
          render: (version: any) => (
            <Switch
              onChange={checked => {
                if (!checked) return;
                handleActive(version?.id);
              }}
              checked={
                versionId === version?.id ||
                (version?.is_active === "TRUE" && versionId === version?.id)
              }
              defaultChecked={version?.is_active === "TRUE"}
            />
          ),
        },
      ] as any)),
  {
    title: "action",
    key: "action",
    width: "15%",
    render: (version: IVersion) => (
      <div
        onClick={() => download(version?.id)}
        className="table__action__btn table__action__btn--client"
      >
        Download
      </div>
    ),
  },
];
